import React, { useMemo } from 'react';
import { HbrDomTable } from '@harbor/elements-react-standalone';
import {
  Column,
  ColumnAlignment,
  ColumnType,
  DataType,
  getColumnTypeConfig,
} from '../../../types/table';
import type { AttainmentDrillNode } from '../../../hooks/attainment-hooks';
import { attainmentPct, formatUsdCompact } from '../../../support/enablementLeader';
import { statsForInterval } from '../../../support/attainmentLeader';

/**
 * One row per direct report in the attainment drill node. Own figures are the person's own plan for the
 * selected interval (read from their per-period detail, so a report with no plan then reads "—" instead
 * of another interval's numbers). A manager's own figures are already their team's plan, so there is no
 * separate team column. Clicking a name drills in.
 */

interface Props {
  node: AttainmentDrillNode;
  /** Interval label the figures must belong to. */
  intervalLabel: string;
  onSelectEmail: (email: string) => void;
}

type RowModel = {
  id: string;
  email: string;
  is_manager: boolean;
  attainment: string;
  bookings: string;
  quota: string;
};

const pctText = (v: number | null) => (v == null ? '—' : `${v}%`);

export default function AttainmentTeamMembersTable({ node, intervalLabel, onSelectEmail }: Props) {
  const data = useMemo<RowModel[]>(
    () =>
      (node.reports || [])
        .map((r) => {
          const own = statsForInterval(r.own, intervalLabel);
          return {
          id: `u_${r.email.toLowerCase().replace(/[^a-z0-9_-]/g, '_')}`,
          email: r.email.toLowerCase(),
          is_manager: r.is_manager,
          attainment: pctText(attainmentPct(own)),
          bookings: formatUsdCompact(own?.bookings),
          quota: formatUsdCompact(own?.quota),
          };
        })
        .sort((a, b) => a.email.localeCompare(b.email)),
    [node, intervalLabel],
  );

  const visibleColumns: Column[] = useMemo(() => {
    const custom = (id: string, name: string, width: string, align: ColumnAlignment): Column => ({
      id,
      name,
      width,
      columnType: ColumnType.Custom,
      sortable: false,
      align,
      dataType: DataType.String,
      resizable: false,
      reorderable: false,
    });
    return [
      custom('email', 'Team Member', '40%', ColumnAlignment.Left),
      custom('attainment', 'Attainment %', '20%', ColumnAlignment.Right),
      custom('bookings', 'Bookings', '20%', ColumnAlignment.Right),
      custom('quota', 'Quota', '20%', ColumnAlignment.Right),
    ]
      .map((c) => getColumnTypeConfig(c))
      .map((c) => ({ ...c, resizable: false, reorderable: false }));
  }, []);

  const renderHeader = () =>
    visibleColumns.map((column) => (
      <div key={`column-${column.id}`} slot={`column-${column.id}`}>
        <div
          className="flex items-center"
          style={{ justifyContent: column.align === ColumnAlignment.Right ? 'flex-end' : 'flex-start' }}
        >
          <div>{column.name}</div>
        </div>
      </div>
    ));

  const renderBody = () => {
    const list: JSX.Element[] = [];
    for (const row of data) {
      for (const column of visibleColumns) {
        const slot = `row-${row.id}-column-${column.id}`;

        if (column.id === 'email') {
          list.push(
            <div
              key={slot}
              slot={slot}
              className="min-h-8 min-w-0 flex items-center font-semibold hover:text-link hover:underline cursor-pointer truncate"
              title={`${row.email}${row.is_manager ? ' (Mgr)' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onSelectEmail(row.email);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelectEmail(row.email);
                }
              }}
              aria-label={`View ${row.email}`}
            >
              {row.email}
              {row.is_manager ? ' (Mgr)' : ''}
            </div>,
          );
          continue;
        }

        list.push(
          <div
            key={slot}
            slot={slot}
            className="min-h-8 flex items-center box-content"
            style={{ justifyContent: 'flex-end', fontVariantNumeric: 'tabular-nums' }}
          >
            {(row as any)[column.id]}
          </div>,
        );
      }
    }
    return list;
  };

  if (!data.length) {
    return <div className="hbr-css__text-secondary-sm">No direct reports found.</div>;
  }

  return (
    <HbrDomTable
      autoLayout={true}
      columns={visibleColumns}
      data={data}
      style={{ display: 'block', width: '100%' }}
    >
      {renderHeader()}
      {renderBody()}
    </HbrDomTable>
  );
}
